import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { SERVER_URL } from '@/env-vars'
import { Store } from '@/store'
import { useAuthFromStorage } from '@/hooks/useAuthStorage'
import { useSyncChat } from '@/mutations/useSyncChat'
import { useChatIdFromUrl } from '@/hooks/useChatIdFromUrl'
import {
	FollowUpsMessage,
	QuizMessage,
	VideoSuggestionMessage,
} from '@/store/types'
import { toast } from 'sonner'
import * as Sentry from '@sentry/react'
import { errors } from '@/i18n/errors'
import { analytics } from '@/lib/analytics'

type EngagementMessage = FollowUpsMessage | QuizMessage | VideoSuggestionMessage | null

export function useEngagement() {
	const { chatId } = useChatIdFromUrl()
	const { authToken } = useAuthFromStorage()
	const syncChat = useSyncChat()

	return useMutation({
		mutationKey: ['engagement', chatId],
		mutationFn: async () => {
			if (!chatId) throw Error('ChatId Not Found')

			const chat = Store.useBoundStore.getState().chats[chatId]
			if (!chat || chat.messages.length < 2) {
				return null
			}

			const messages = chat.messages.slice(-5)

			const response = await axios.post(
				`${SERVER_URL}/chat/engagement`,
				{ messages },
				{
					headers: { Authorization: `Bearer ${authToken}` },
				},
			)

			const engagement = response.data as EngagementMessage
			return { engagement, chatId }
		},
		onSuccess: (data) => {
			if (!data || !data.engagement) return

			Store.appendMessagesToChat({
				chatId: data.chatId,
				messages: [data.engagement],
			})

			// Track engagement by type
			const engagementType = data.engagement.type
			if (engagementType === 'quiz') {
				analytics.quizStarted()
			} else if (engagementType === 'followup_suggestions') {
				// Track that follow-ups were shown
				Sentry.captureEvent({
					message: 'Follow-up suggestions shown',
					level: 'info',
					tags: {
						type: 'engagement',
						id: 'follow-ups-shown',
					},
				})
			} else if (engagementType === 'video_suggestion') {
				analytics.videoRecommendationRequested()
			}

			syncChat.mutate()
		},
		onError: (error) => {
			toast(errors.VIDEO_SUGGESTION_ERROR, {
				dismissible: true,
				action: {
					label: 'Ok',
					onClick: () => {},
				},
				duration: 5 * 1000,
			})
			Sentry.captureException(error, {
				tags: {
					type: 'api-call',
					id: 'engagement',
				},
			})
		},
	})
}
